import { useEffect, useMemo, useState, type RefObject } from "react";
import { PANEL_DIVIDER_PX, usePanelWidths, type PanelWidthsConfig } from "@/shared/ui/panelWidths";

/**
 * Larghezze persistite delle colonne della pagina Messaggi (2026-07-02,
 * Davide: "posando il mouse nella riga divisoria deve essere possibile
 * aggiustare gli spazi a piacimento, e devono rimanere salvati").
 *
 * La colonna chat resta fluida (minmax(0,1fr)); si ridimensionano sidebar
 * filtri, lista thread e ispettore contesto. Meccanica condivisa in
 * shared/ui/panelWidths (riusata dalla pagina Mail, proposta A).
 */
export const MESSAGGI_PANEL_DEFAULTS = {
  sidebar: 188,
  threads: 300,
  context: 280,
} as const satisfies Record<string, number>;

export type MessaggiPanelKey = keyof typeof MESSAGGI_PANEL_DEFAULTS;

export const MESSAGGI_PANEL_BOUNDS: Record<MessaggiPanelKey, { min: number; max: number }> = {
  sidebar: { min: 150, max: 340 },
  threads: { min: 220, max: 520 },
  context: { min: 200, max: 480 },
};

export const MESSAGGI_PANEL_STORAGE_KEY = "om.messaggi.panel-widths.v1";

export { PANEL_DIVIDER_PX };

/**
 * Larghezza della GRIGLIA sotto la quale l'ispettore contesto lascia la
 * griglia e diventa un cassetto (Davide 2026-08-24, "da portatile non è
 * ottimizzato").
 *
 * Il numero viene dalla misura, non dal gusto: le tre colonne fisse più i
 * divisori occupano 780px, quindi a 1330px di griglia alla chat ne restano
 * ~550, che è il minimo per lavorarci. Sotto, precipita: a 1294 (finestra
 * 1366, il portatile più diffuso) 514px, a 1208 → 428, a 952 → 172.
 * `minmax(0,1fr)` fa sì che il difetto non si veda come barra di scorrimento:
 * la chat si schiaccia e basta.
 *
 * ⚠️ Si confronta con la larghezza della GRIGLIA, non della finestra: la barra
 * di navigazione ne mangia ~72px, e legarsi alla finestra vorrebbe dire
 * codificare quel 72 in due posti. Prima era una `matchMedia('min-width:
 * 1400px')`: tolta il 2026-08-25 perché l'evento `change` **non arriva** dove
 * i frame non vengono composti (misurato: a 1024 `matches` era già `false` ma
 * React vedeva ancora `true`, e il contesto restava in colonna con la chat a
 * 172px). Una misura sola, e aggiornata da due sorgenti indipendenti.
 */
export const MESSAGGI_CONTESTO_IN_COLONNA_PX = 1330;

/** Sotto questa larghezza la chat non è più leggibile: è il pavimento che
 *  `adattaLarghezze` difende togliendo spazio alle colonne fisse. */
export const CHAT_MIN_PX = 460;

const CONFIG: PanelWidthsConfig<MessaggiPanelKey> = {
  storageKey: MESSAGGI_PANEL_STORAGE_KEY,
  defaults: MESSAGGI_PANEL_DEFAULTS,
  bounds: MESSAGGI_PANEL_BOUNDS,
};

/**
 * Restringe le colonne fisse quanto basta perché alla chat restino almeno
 * `CHAT_MIN_PX`, senza mai scendere sotto il minimo di ciascuna.
 *
 * Non tocca le larghezze SALVATE: adatta solo ciò che viene disegnato. Serve
 * proprio al caso del portatile — le larghezze vivono in localStorage per
 * postazione, ma la postazione è la stessa quando il portatile è al monitor
 * grande e quando è sulle ginocchia: le colonne allargate sul monitor
 * seguirebbero fin dentro lo schermo piccolo, e ricollegando il monitor
 * l'utente ritroverebbe il layout che si era scelto.
 *
 * Chi ha già ceduto tutto (colonna al minimo) non cede altro: il taglio è
 * proporzionale al margine che ogni colonna ha SOPRA il proprio minimo.
 */
export function adattaLarghezze(
  widths: Record<MessaggiPanelKey, number>,
  spazioDisponibile: number,
  conContesto: boolean,
): Record<MessaggiPanelKey, number> {
  if (!Number.isFinite(spazioDisponibile) || spazioDisponibile <= 0) return widths;
  const chiavi: MessaggiPanelKey[] = conContesto
    ? ["sidebar", "threads", "context"]
    : ["sidebar", "threads"];
  const divisori = PANEL_DIVIDER_PX * chiavi.length;
  const fisse = chiavi.reduce((totale, k) => totale + widths[k], 0);
  const eccesso = fisse + divisori + CHAT_MIN_PX - spazioDisponibile;
  if (eccesso <= 0) return widths;

  const margine = chiavi.reduce(
    (totale, k) => totale + Math.max(0, widths[k] - MESSAGGI_PANEL_BOUNDS[k].min),
    0,
  );
  if (margine <= 0) return widths;

  // Oltre il margine disponibile non si va: sotto i minimi il contenuto
  // troncherebbe peggio di quanto guadagni la chat.
  const quota = Math.min(1, eccesso / margine);
  const adattate = { ...widths };
  for (const k of chiavi) {
    const cedibile = Math.max(0, widths[k] - MESSAGGI_PANEL_BOUNDS[k].min);
    adattate[k] = Math.round(widths[k] - cedibile * quota);
  }
  return adattate;
}

/**
 * Larghezza utile in px dell'elemento passato. `ResizeObserver` quando c'è
 * (browser), altrimenti la finestra: in jsdom l'observer non esiste e senza
 * questo ripiego i test renderebbero con larghezza 0.
 */
export function useLarghezzaElemento(ref: RefObject<HTMLElement | null>): number {
  const [larghezza, setLarghezza] = useState(() =>
    typeof window === "undefined" ? 0 : window.innerWidth,
  );

  useEffect(() => {
    if (typeof window === "undefined") return;

    const misura = (): void => {
      const misurata = ref.current?.getBoundingClientRect().width ?? 0;
      // `0` non vuol dire "nessuno spazio": vuol dire che nessuno ha ancora
      // disposto l'elemento — prima del primo layout, o in un ambiente senza
      // layout come jsdom. Prenderlo per buono stringerebbe tutte le colonne
      // al minimo per un istante (o per sempre, nei test).
      setLarghezza(misurata > 0 ? misurata : window.innerWidth);
    };
    misura();

    // Due sorgenti, non una. `ResizeObserver` e' la piu' precisa (coglie anche
    // i cambi che la finestra non vede, come una colonna trascinata), ma le sue
    // notifiche viaggiano col ciclo di disegno: dove i frame non vengono
    // composti — pannello nascosto, scheda in secondo piano, browser headless —
    // non arrivano, e il layout resta congelato sull'ultima misura. Successo il
    // 2026-08-24: allargando da 820 a 1280 le colonne restavano ai minimi.
    // `resize` non ha quella dipendenza e costa due righe.
    window.addEventListener("resize", misura);
    const observer =
      typeof ResizeObserver === "undefined" ? null : new ResizeObserver(() => misura());
    if (observer && ref.current) observer.observe(ref.current);

    return () => {
      window.removeEventListener("resize", misura);
      observer?.disconnect();
    };
  }, [ref]);

  return larghezza;
}

export function useMessaggiPanelWidths(
  /** `false` quando l'ispettore contesto è nel cassetto: la sua colonna non
   *  entra nella griglia e non partecipa al calcolo dello spazio. */
  conContesto = true,
  /** Larghezza utile della griglia. `0`/omessa = nessun adattamento (prima
   *  misura, o test che non si occupano di layout). */
  spazioDisponibile = 0,
): {
  widths: Record<MessaggiPanelKey, number>;
  setWidth: (key: MessaggiPanelKey, px: number) => void;
  resetWidth: (key: MessaggiPanelKey) => void;
  gridTemplateColumns: string;
} {
  const { widths: salvate, setWidth, resetWidth } = usePanelWidths(CONFIG);

  const widths = useMemo(
    () => adattaLarghezze(salvate, spazioDisponibile, conContesto),
    [salvate, spazioDisponibile, conContesto],
  );

  const gridTemplateColumns = useMemo(() => {
    const colonne = [
      `${widths.sidebar}px`,
      `${PANEL_DIVIDER_PX}px`,
      `${widths.threads}px`,
      `${PANEL_DIVIDER_PX}px`,
      "minmax(0, 1fr)",
    ];
    if (conContesto) colonne.push(`${PANEL_DIVIDER_PX}px`, `${widths.context}px`);
    return colonne.join(" ");
  }, [widths, conContesto]);

  // `setWidth` riceve px calcolati dal trascinamento sulla larghezza ADATTATA;
  // riscriverli così com'è è giusto: l'utente sta scegliendo su ciò che vede.
  // È già memoizzata in `usePanelWidths` (shared/ui/panelWidths.ts): il
  // `useCallback(setWidth, [setWidth])` che stava qui restituiva sempre la
  // funzione che riceveva — deps invariate → cache = setWidth, deps cambiate
  // → la nuova setWidth. Non stabilizzava nulla.
  return { widths, setWidth, resetWidth, gridTemplateColumns };
}
